import { Button, Icons, Input } from '@healthinal/ui';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { PatientDtoGender } from '../../../../api/generated/model/index.ts';
import { IPersonListItem, useGetPersonsQuery } from '../../../../api/generated/person-controller/person-controller.ts';
import { queryClient } from '../../../../api/queryClient.ts';
import { TruncateText } from '../../../../components/TruncateText.tsx';
import { formatDate } from '../../../../utils/date.ts';
import { getGenderName } from '../../../../utils/gender.ts';
import { getPreferredPhoneNumber } from '../../../../utils/patient.ts';
import { getSalutationName } from '../../../../utils/salutation.ts';
import { CreatePersonData } from '../CreatePersonData.tsx';
import { MasterdataTable } from './MasterTable.tsx';

const DEBOUNCE_MS = 300;

export function PersonalDataTableHeader() {
  return (
    <>
      <th style={{ width: 80 }}>Anrede</th>
      <th style={{ width: 140 }}>Nachname</th>
      <th style={{ width: 140 }}>Vorname</th>
      <th style={{ width: 125 }}>Geburtsdatum</th>
      <th style={{ width: 110 }}>Geschlecht</th>
      <th style={{ width: 150 }}>Telefon Nr.</th>
      <th style={{ width: 220 }}>E-Mail-Adresse</th>
    </>
  );
}

export function PersonalDataTableData({ patient }: { patient?: IPersonListItem }) {
  return (
    <>
      <td>
        <TruncateText>{getSalutationName(patient?.salutation)}</TruncateText>
      </td>
      <td>
        <TruncateText>{patient?.lastName ?? '-'}</TruncateText>
      </td>
      <td>
        <TruncateText>{patient?.firstName ?? '-'}</TruncateText>
      </td>
      <td>
        <TruncateText>{formatDate(patient?.birthDate)}</TruncateText>
      </td>
      <td>
        <TruncateText>{getGenderName(patient?.gender as PatientDtoGender)}</TruncateText>
      </td>
      <td>
        <TruncateText>{getPreferredPhoneNumber(patient)}</TruncateText>
      </td>
      <td>
        <TruncateText>{patient?.email ?? '-'}</TruncateText>
      </td>
    </>
  );
}

export function PersonalDataTable() {
  const navigate = useNavigate();
  const [isCreatePersonOpen, setIsCreatePersonOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [size] = useState(20);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, DEBOUNCE_MS);
    return () => clearTimeout(timeout);
  }, [search]);

  const { data, isLoading, isFetching } = useGetPersonsQuery(
    { page, size, search: debouncedSearch || undefined },
    true,
    { refetchOnMount: 'always' },
  );

  const patients = data?.content ?? [];
  const totalCount = data?.totalCount ?? 0;
  const currentPage = data?.page ?? page;
  const currentSize = data?.size ?? size;
  const totalPages = currentSize > 0 ? Math.ceil(totalCount / currentSize) : 0;

  return (
    <>
      <MasterdataTable
        title="Personenliste"
        headerActions={
          <>
            <Input
              placeholder="Suche..."
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(0);
              }}
              sx={{
                width: 260,
                maxWidth: '100%',
                flexShrink: 0,
              }}
            />
            <Button onClick={() => setIsCreatePersonOpen(true)} endDecorator={<Icons.Add />}>
              Person erfassen
            </Button>
          </>
        }
        isLoading={isLoading || isFetching}
        isEmpty={patients.length === 0}
        emptyText="Keine Daten vorhanden."
        colSpan={7}
        page={currentPage}
        totalPages={totalPages}
        onPrevPage={() => setPage((prev) => Math.max(0, prev - 1))}
        onNextPage={() => setPage((prev) => Math.min(Math.max(0, totalPages - 1), prev + 1))}
        disablePrev={currentPage === 0 || isLoading || isFetching}
        disableNext={currentPage + 1 >= totalPages || isLoading || isFetching}
        header={<PersonalDataTableHeader />}>
        {patients.map((patient, index) => (
          <tr
            key={patient.id ?? `${patient.firstName}-${patient.lastName}-${index}`}
            onClick={() => {
              if (!patient.id) return;

              navigate({
                to: '/masterdata/persons/$personId',
                params: { personId: patient.id },
              });
            }}>
            <PersonalDataTableData patient={patient} />
          </tr>
        ))}
      </MasterdataTable>

      <CreatePersonData
        open={isCreatePersonOpen}
        onClose={() => setIsCreatePersonOpen(false)}
        onCreated={() => {
          queryClient.invalidateQueries({ queryKey: ['persons'] });
        }}
      />
    </>
  );
}
